import { useRef, useState, useEffect } from "react";
import { parse, isValid } from "date-fns";

const pad2 = (value) => String(value).padStart(2, "0");

const formatValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isValid(date)) return `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`;
  return "";
};

const maskDigits = (rawValue, appendSeparator) => {
  const digits = rawValue.replace(/\D/g, "").slice(0, 8);
  let masked = digits.slice(0, 2);
  if (digits.length > 2 || (digits.length === 2 && appendSeparator)) masked += `/${digits.slice(2, 4)}`;
  if (digits.length > 4 || (digits.length === 4 && appendSeparator)) masked += `/${digits.slice(4, 8)}`;
  return masked;
};

const caretAfterDigits = (masked, digitCount) => {
  if (digitCount <= 0) return 0;
  let seen = 0;
  for (let index = 0; index < masked.length; index += 1) {
    if (masked[index] >= "0" && masked[index] <= "9") {
      seen += 1;
      if (seen === digitCount) {
        let position = index + 1;
        while (position < masked.length && masked[position] === "/") position += 1;
        return position;
      }
    }
  }
  return masked.length;
};

const DateInput = ({ value, onChange, placeholder = "", className = "", ghost = false, disabled = false, hasError = false, hasWarning = false, isRequiredEmpty = false, ...rest }) => {
  const inputRef = useRef(null);
  const shortcutOffsetRef = useRef(0);
  const [isInError, setIsInError] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(formatValue(value));

  useEffect(() => {
    const formattedValue = formatValue(value);
    setInputValue((previousInputValue) => {
      if (formattedValue === previousInputValue) return previousInputValue;
      if (formattedValue === "" && previousInputValue.length < 10) return previousInputValue;
      return formattedValue;
    });
  }, [value]);

  const onShortcutKeyDown = (event) => {
    if (event.ctrlKey || event.metaKey) return;
    if (event.key !== " " && event.key !== "-" && event.key !== "+") return;
    event.preventDefault();
    if (event.key === " ") shortcutOffsetRef.current = 0;
    if (event.key === "-") shortcutOffsetRef.current -= 1;
    if (event.key === "+") shortcutOffsetRef.current += 1;
    const date = new Date();
    date.setDate(date.getDate() + shortcutOffsetRef.current);
    setIsInError(false);
    setInputValue(formatValue(date));
    onChange(`${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`);
  };

  const onInputChange = (event) => {
    shortcutOffsetRef.current = 0;
    setIsInError(false);
    const inputElement = event.target;
    const rawValue = inputElement.value;
    const caret = typeof inputElement.selectionStart === "number" ? inputElement.selectionStart : rawValue.length;
    const digitsBeforeCaret = rawValue.slice(0, caret).replace(/\D/g, "").length;

    const maskedValue = maskDigits(rawValue, rawValue.length >= inputValue.length);
    const nextCaret = caretAfterDigits(maskedValue, digitsBeforeCaret);

    setInputValue(maskedValue);
    inputElement.value = maskedValue;
    inputElement.setSelectionRange(nextCaret, nextCaret);

    if (maskedValue.length === 0) return onChange("");
    if (maskedValue.length !== 10) return onChange(null);
    if (isValid(parse(maskedValue, "dd/MM/yyyy", new Date())) === true) {
      onChange(`${maskedValue.slice(6, 10)}-${maskedValue.slice(3, 5)}-${maskedValue.slice(0, 2)}`);
      return;
    }
    setIsInError(true);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        maxLength={10}
        placeholder="JJ/MM/AAAA"
        className={`${className} w-full rounded-md border px-2 py-1 ${
          isInError || hasError ? "border-red-500 bg-red-50" : hasWarning ? "border-yellow-500 bg-yellow-50" : isRequiredEmpty ? "border-orange-300 bg-orange-50" : ghost ? "border-transparent" : "border-gray-200"
        } ${
          ghost
            ? `h-[32px] text-sm outline-none transition-colors focus:ring-2 ${
                isRequiredEmpty
                  ? "placeholder:text-orange-400 focus:ring-orange-400"
                  : "placeholder:text-gray-300 hover:border-gray-200 hover:bg-gray-50 focus:border-primary-500 focus:bg-white focus:ring-primary-500/20"
              }`
            : "h-[36px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1"
        }${disabled ? " disabled:cursor-not-allowed disabled:bg-gray-200 disabled:opacity-50" : ""}`}
        value={inputValue}
        onChange={onInputChange}
        onKeyDown={onShortcutKeyDown}
        onFocus={() => {
          shortcutOffsetRef.current = 0;
          setIsFocused(true);
        }}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
        {...rest}
      />
      {!ghost && placeholder && (isFocused || inputValue.length > 0) && (
        <div className="absolute left-2 -top-2 bg-white px-1 text-xs text-gray-500 pointer-events-none">{placeholder}</div>
      )}
    </div>
  );
};

export default DateInput;
